import { User, Bell } from 'lucide-react'
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDroneMapStore } from '../stores/droneMapStore';
import { useCompanyDetails } from '../cacheQueries';
import LanguageSelector from './LanguageSelector';
import OrgSwitcher from './OrgSwitcher';
import { configEnv } from './configEnv';
import { BellNotification } from '../utils/notificationUtils';
import { noteBellSwipeHintShown, shouldHintBellSwipe } from '../utils/notificationUtils';
import { useBellCards } from '../hooks/useBellCards';
import { BellNotificationCard } from './DroneCard';
import { isStagingHost } from '../utils/staging';



export default function TopNavigation() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // Org-keyed + guard-removed company details (G3). TopNav renders above the
    // keyed <Outlet>, so a bare fetch on [user] never re-fired on a no-reload
    // org switch — leaving `isPrivate` (which gates the OrgSwitcher) stale.
    // useCompanyDetails re-keys on the active org so it stays correct.
    const { data: companyDetails } = useCompanyDetails();
    const [bellOpen, setBellOpen] = useState(false);
    // Today's flights: backend detection rows merged with the in-progress flight
    // the WebSocket is reporting. Not just what this tab happened to witness.
    const { cards: bellNotifications, loadFailed, retry, dismiss, clearAll } = useBellCards();
    const bellRef = useRef<HTMLDivElement | null>(null);
    // Decided once per opening, not per render: re-evaluating mid-panel would
    // restart the nudge on every list update.
    const [hintSwipe, setHintSwipe] = useState(false);

    // Via configEnv so unset build envs fall back to AirPlate (logo + name)
    // instead of rendering "undefined Logo" with a broken <img>.
    const { clientID, clientName, clientLogo: clientImage } = configEnv;

    const isPrivate = companyDetails?.organization?.private;
    const hasNotifications = bellNotifications.length > 0;

    useEffect(() => {
      if (!bellOpen) {
        setHintSwipe(false);
        return;
      }
      const hint = shouldHintBellSwipe();
      setHintSwipe(hint);
      if (hint) noteBellSwipeHintShown();
    }, [bellOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (!bellOpen) return;
        if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
          setBellOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [bellOpen]);

    // Dismissal is recorded per flight, not by deleting from a list — the cards
    // are derived from today's rows, so a deleted one would simply come back on
    // the next render.
    const handleDismiss = (id: string) => dismiss(id);
    const handleClearAll = () => clearAll();

    const handleNavigate = (notification: BellNotification) => {
      // Intentionally do NOT remove the bell entry on navigate. Users dismiss via ✕
      // (handleDismiss) or "Clear All". Keeping the entry preserves the detection
      // timestamp, which anchors the trail-fetch window on subsequent map clicks.
      setBellOpen(false);

      const isOnMapPage = window.location.pathname === '/map';

      // Look up the full WS message from the store if the drone is still live
      const store = useDroneMapStore.getState();
      const droneMsg = store.latestMessages.get(notification.mac_address ?? '') ?? null;

      if (isOnMapPage && notification.longitude !== undefined && notification.latitude !== undefined) {
        store.setFocusDrone(
          notification.mac_address ?? null,
          notification.uas_id ?? null,
          notification.protocol_type ?? (droneMsg?.type ?? null),
          droneMsg,
          notification.longitude,
          notification.latitude,
          notification.operator_longitude,
          notification.operator_latitude,
          notification.operator_location_type,
          notification.timestamp,
          undefined,
          notification.sessionStart,
        );
        window.dispatchEvent(new CustomEvent('droneNotificationZoom', {
          detail: {
            longitude: notification.longitude,
            latitude: notification.latitude,
            serialNumber: notification.uas_id,
            mac_address: notification.mac_address,
          }
        }));
      } else {
        store.setFocusDrone(
          notification.mac_address ?? null,
          notification.uas_id ?? null,
          notification.protocol_type ?? (droneMsg?.type ?? null),
          droneMsg,
          notification.longitude,
          notification.latitude,
          notification.operator_longitude,
          notification.operator_latitude,
          notification.operator_location_type,
          notification.timestamp,
          undefined,
          notification.sessionStart,
        );
        navigate('/map', {
          replace: true,
          state: {
            fromNewDashboard: true,
            longitude: notification.longitude,
            latitude: notification.latitude,
          },
        });
      }
    };

    return (
        <>
        {/* Left padding has to match the hamburger button's hide breakpoint.
            HAMBURGER_BUTTON uses `lg:hidden`, so the burger renders at
            left-2 (8px) on every viewport < 1024px. Previously the
            padding shrank from pl-14 → sm:pl-4 at 640px, which pushed
            the AirPlate logo into the burger's 8–32px region for the
            entire 640–1023px range (visible in Opera, whose native
            sidebar pulls the page viewport below 1024px even on a
            wide-OS window). Keep pl-14 until lg+ where the burger is
            actually hidden. */}
        <div className={`z-[100] fixed left-0 top-0 w-full h-12 bg-primary dark:bg-dark-primary flex justify-start text-white items-center px-3 pl-14 lg:px-4 lg:pl-4 *:font-heading *:tracking-[.5px] *:antialiased
          ${clientID === 'default' ? ('*:font-bold text-[16px]') : ('*:font-normal text-[22px]')}
          `}>
            {clientID === 'default' ? (
              <>
              <a href="/">
                <img className='h-6 sm:h-7 lg:h-8 mr-2 ml-0 lg:mr-4 max-w-[110px] sm:max-w-[140px] lg:max-w-none object-contain' src={clientImage} alt={`${clientName} Logo`} />
              </a>
              <span className='hidden lg:inline !text-[18px]'>{t('nav.droneDashboard', 'Drone Dashboard')}</span>
              </>
            ) :
              <>
              <img
                className={clientID === 'aerofence'
                  // AeroFence's logo is wide (shield and wordmark): taller,
                  // and not capped in width.
                  ? 'h-8 lg:h-10 mr-2 ml-0 lg:mr-10 max-w-none object-contain'
                  : 'h-6 sm:h-7 lg:h-8 mr-2 ml-0 lg:mr-10 max-w-[110px] sm:max-w-[150px] lg:max-w-none object-contain'}
                src={clientImage}
                alt={`${clientName} Logo`}
              />
              {/* MyDefence's tagline; AeroFence has none. */}
              {clientID !== 'aerofence' && (
                <span className='hidden lg:inline text-[21px]'>{t('nav.counterDroneTech', 'Counter Drone Technology')}</span>
              )}
              </>
            }
            {/* Whatever the client branding, staging.airplate.dk says so. */}
            {isStagingHost() && (
              <span className='ml-2 rounded-full px-2 text-[10px] font-bold uppercase bg-white/15 text-white'>Staging</span>
            )}
            <div className='flex-grow'></div>
            <div className={`flex shrink-0 justify-end items-center space-x-2 ${clientID === 'default' ? ('text-[16px] *:font-bold') : ('text-[19px] *:font-normal')}`}>
                {companyDetails?.currentUser?.username ? (
                  <>
                  {/* <div className="bg-transparent text-foreground flex flex-col items-center justify-center">
                    <DarkModeToggle />
                  </div> */}
                  <LanguageSelector onBeforeOpen={() => setBellOpen(false)} />
                  <div ref={bellRef} className="relative flex items-center">
                    <button
                      aria-label={t('notifications.notifications', 'Notifications')}
                      className="relative flex items-center justify-center rounded-full bg-primary dark:bg-dark-primary p-1 transition border border-transparent focus:outline-none focus:ring-0"
                      onClick={() => setBellOpen(!bellOpen)}
                    >
                      <Bell className='w-5 h-5' />
                      {hasNotifications ? (
                        <span className="absolute -top-1 -right-1 min-w-[16px] h-4 rounded-full bg-red-500 text-[10px] leading-4 text-center px-[4px]">
                          {bellNotifications.length}
                        </span>
                      ) : null}
                    </button>
                    {bellOpen ? (
                      <div
                        className="
                          fixed top-12 left-2 right-2
                          sm:absolute sm:top-full sm:mt-2 sm:left-1/2 sm:-translate-x-1/2 sm:w-80
                          max-w-[calc(100vw-1rem)]
                          bg-white/95 dark:bg-slate-900 text-zinc-900 dark:text-zinc-100 rounded-xl shadow-2xl border border-zinc-200 dark:border-slate-700 backdrop-blur z-[120]
                          overflow-hidden
                        "
                      >
                        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-200 dark:border-slate-700 bg-zinc-50/80 dark:bg-slate-800/60">
                          <span className="font-semibold text-sm">{t('notifications.notifications', 'Notifications')}</span>
                        <div className="flex items-center space-x-3">
                          {hasNotifications ? (
                            <button
                              className="text-[11px] font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md min-w-[100px] px-4 py-1.5 border border-transparent focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-0"
                              onClick={handleClearAll}
                            >
                              {t('common.clear', 'Clear')}
                            </button>
                          ) : null}
                        </div>
                      </div>
                        {/* overscroll-contain stops scroll chaining: without it a
                            wheel gesture that this list can't consume — the
                            off-axis part of a sideways swipe, or a scroll past
                            either end — passes to the page behind and scrolls
                            the whole view out from under the open panel. */}
                        <div className="max-h-80 overscroll-contain overflow-y-auto p-2 space-y-2">
                          {hasNotifications ? (
                            bellNotifications.map((notification, index) => (
                              <BellNotificationCard
                                key={notification.id}
                                notification={notification}
                                onOpen={() => handleNavigate(notification)}
                                onDismiss={() => handleDismiss(notification.id)}
                                // Only the top card nudges: every card twitching
                                // at once reads as a glitch, not an invitation.
                                hintDismissAffordance={index === 0 && hintSwipe}
                              />
                            ))
                          ) : loadFailed ? (
                            // A failed fetch is not an empty day — say so, and
                            // offer the retry the query layer won't do on its own.
                            <div className="px-3 py-4 text-sm text-zinc-600">
                              <p>{t('notifications.loadFailed', "Couldn't load today's flights.")}</p>
                              <button
                                type="button"
                                className="mt-2 rounded-md bg-zinc-100 px-3 py-1.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-200"
                                onClick={retry}
                              >
                                {t('common.retry', 'Retry')}
                              </button>
                            </div>
                          ) : (
                            <div className="px-3 py-4 text-sm text-zinc-500">{t('notifications.noNotifications', 'No notifications')}</div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <User className='w-5 h-5 max-sm:hidden' />
                  <span className='capitalize max-sm:hidden'>{companyDetails.currentUser.username}</span>
                  {!isPrivate && companyDetails?.organization?.name ? (
                      <>
                      <span className='mb-1 font-thin max-sm:hidden'>|</span>
                      <OrgSwitcher
                        fallbackName={companyDetails.organization.name}
                        onBeforeOpen={() => setBellOpen(false)}
                      />
                      </>
                  ) : null}
                  </>
                ) : null}
            </div>
        </div>
        </>
    );
}
